'use client';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { LoginBrandHeader } from '../../../components/LoginBrandHeader';
import { textOn } from '../../../lib/brand-color';
import { brandingMerchantId, loginBrandingOf, type LoginBranding } from '../../../lib/login-branding';
import LegacyStyle from '../../../components/LegacyStyle';

const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api';

type LoginType = 'merchant' | 'manager' | 'staff';

// Libellé, couleur et accroche de chaque type de compte. Gérant (#380) : une ou plusieurs succursales.
// Couleur de chaque profil et texte lisible dessus, en clair comme en sombre (tokens, #596).
const LOGIN_TYPES: Record<LoginType, { label: string; color: string; fg: string; tagline: string }> = {
  merchant: { label: 'Commerçant', color: 'var(--accent-strong)', fg: '#fff', tagline: 'Gérez votre programme de fidélité en toute simplicité.' },
  manager: { label: 'Gérant', color: 'var(--warn)', fg: 'var(--warn-fg)', tagline: 'Suivez les chiffres et les clients de vos succursales.' },
  staff: { label: 'Staff', color: 'var(--ok)', fg: 'var(--ok-fg)', tagline: 'Scannez et validez les points en boutique.' },
};

const StoreIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 7l2-4h16l2 4" /><path d="M2 7v2a3 3 0 0 0 6 0V7a3 3 0 0 0 6 0v2a3 3 0 0 0 6 0V7" /><path d="M4 11v9h16v-9" /><path d="M9 20v-5h6v5" />
  </svg>
);

const ScanIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 7V5a2 2 0 0 1 2-2h2" /><path d="M17 3h2a2 2 0 0 1 2 2v2" /><path d="M21 17v2a2 2 0 0 1-2 2h-2" /><path d="M7 21H5a2 2 0 0 1-2-2v-2" /><line x1="7" y1="12" x2="17" y2="12" />
  </svg>
);

const EyeIcon = ({ off }: { off?: boolean }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {off ? (
      <>
        <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" /><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" /><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" /><line x1="2" y1="2" x2="22" y2="22" />
      </>
    ) : (
      <>
        <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

function friendlyError(status: number | null, apiMessage: string): string {
  if (status === 401 || status === 403) return 'Email ou mot de passe incorrect.';
  if (status === 429) return 'Trop de tentatives. Réessayez dans quelques minutes.';
  if (status === null || status >= 500) return 'Le service est momentanément indisponible. Réessayez dans un instant.';
  return apiMessage || 'Échec de la connexion. Vérifiez vos identifiants.';
}

export default function UnifiedLoginPage() {
  const router = useRouter();
  const [loginType, setLoginType] = useState<LoginType>('merchant');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  // Page brandée (#37) : `?merchant=<id>`. `brandingPending` masque l'en-tête générique le temps de la
  // lecture, pour ne pas le voir clignoter ; tout échec (404, réseau, corps inattendu) garde la page générique.
  const [branding, setBranding] = useState<LoginBranding | null>(null);
  const [brandingPending, setBrandingPending] = useState(false);

  useEffect(() => {
    const merchantId = brandingMerchantId(window.location.search);
    if (!merchantId) return;
    const controller = new AbortController();
    setBrandingPending(true);
    fetch(`${API}/merchants/${encodeURIComponent(merchantId)}/branding`, { signal: controller.signal })
      .then(r => (r.ok ? r.json() : null))
      .then(body => setBranding(loginBrandingOf(body)))
      .catch(() => {})
      .finally(() => { if (!controller.signal.aborted) setBrandingPending(false); });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const savedType = localStorage.getItem('last_login_type');
    if (savedType === 'merchant' || savedType === 'manager' || savedType === 'staff') {
      setLoginType(savedType);
      const savedEmail = localStorage.getItem(`last_login_email_${savedType}`);
      if (savedEmail) setEmail(savedEmail);
    }
  }, []);

  const switchLoginType = (type: LoginType) => {
    if (type === loginType) return;
    setLoginType(type);
    setError('');
    localStorage.setItem('last_login_type', type);
    // Restore the email last used for this type; never touch the password field
    const savedEmail = localStorage.getItem(`last_login_email_${type}`);
    setEmail(savedEmail || '');
  };

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const endpoint = loginType;
      const res = await fetch(`${API}/auth/${endpoint}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      let data: any = null;
      try {
        const bodyText = await res.text();
        try { data = JSON.parse(bodyText); } catch { data = { message: bodyText }; }
      } catch { /* empty body */ }

      if (!res.ok) throw Object.assign(new Error(data?.message || res.statusText), { status: res.status });

      localStorage.setItem('token', data.access_token);
      localStorage.setItem('last_login_type', loginType);
      localStorage.setItem(`last_login_email_${loginType}`, email);

      // Session expirée sur le dashboard (#403) : retour à l'écran quitté (onglet, succursale, période),
      // seulement s'il s'agit du dashboard de ce même commerce.
      const back = (merchantId: string) => {
        let saved = '';
        try { saved = sessionStorage.getItem('login-return') || ''; sessionStorage.removeItem('login-return'); } catch {}
        return saved.startsWith(`/merchant/${merchantId}?`) ? saved : `/merchant/${merchantId}`;
      };
      if (loginType === 'merchant') {
        localStorage.removeItem('staff'); // évite un vieux filtre "succursale" resté d'une session précédente
        localStorage.removeItem('manager');
        localStorage.setItem('merchant', JSON.stringify(data.merchant));
        router.push(back(data.merchant.id));
      } else if (loginType === 'manager') {
        // Gérant (#380) : le dashboard de ses succursales ; la portée est imposée par l'API.
        localStorage.removeItem('staff');
        localStorage.removeItem('merchant');
        localStorage.setItem('manager', JSON.stringify(data.manager));
        router.push(back(data.manager.merchantId));
      } else {
        localStorage.removeItem('manager');
        localStorage.removeItem('merchant');
        localStorage.setItem('staff', JSON.stringify(data.staff));
        localStorage.setItem('merchantId', data.staff.merchantId);
        router.push('/scan');
      }
    } catch (err: any) {
      setError(friendlyError(typeof err?.status === 'number' ? err.status : null, err?.message || ''));
      setLoading(false);
    }
  }

  // Page brandée : la couleur du marchand remplace celle de chaque onglet (bordure, onglet actif, bouton),
  // avec un texte lisible dessus (#37).
  const brandColor = branding?.brandColor ?? null;
  const tabColor = (type: LoginType) => brandColor ?? LOGIN_TYPES[type].color;
  const onAccent = brandColor ? textOn(brandColor) : LOGIN_TYPES[loginType].fg;
  const accent = tabColor(loginType);

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: '1.5rem 1rem' }}>
      <div style={{ width: '100%', maxWidth: '440px' }}>

        {/* Welcome header, outside the card for breathing room */}
        {branding ? (
          <LoginBrandHeader branding={branding} />
        ) : (
          <div style={{ textAlign: 'center', marginBottom: '1.5rem', visibility: brandingPending ? 'hidden' : 'visible' }}>
            <div className="logo" style={{ fontSize: '1.75rem', marginBottom: '0.375rem' }}>Bon retour&nbsp;!</div>
            <p style={{ color: 'var(--muted)', fontSize: '0.9rem', margin: 0 }}>
              Heureux de vous revoir. Connectez-vous pour continuer.
            </p>
          </div>
        )}

        <div className="card" style={{ borderTop: `4px solid ${accent}`, transition: 'border-color 0.2s' }}>

          {/* Login Type Toggle */}
          <div role="tablist" aria-label="Type de compte" style={{ display: 'flex', background: 'var(--bg)', borderRadius: '8px', padding: '4px', marginBottom: '1.25rem' }}>
            {(['merchant', 'manager', 'staff'] as LoginType[]).map(type => {
              const active = loginType === type;
              const color = tabColor(type);
              return (
                <button
                  key={type}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => switchLoginType(type)}
                  style={{
                    flex: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
                    padding: '0.625rem 1rem',
                    minHeight: '44px',
                    borderRadius: '6px',
                    border: 'none',
                    background: active ? color : 'transparent',
                    color: active ? onAccent : 'var(--muted)',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'background 0.2s, color 0.2s',
                  }}
                >
                  {type === 'staff' ? <ScanIcon /> : <StoreIcon />}
                  {LOGIN_TYPES[type].label}
                </button>
              );
            })}
          </div>

          <p style={{ color: 'var(--muted)', fontSize: '0.85rem', textAlign: 'center', margin: '0 0 1.25rem' }}>
            {LOGIN_TYPES[loginType].tagline}
          </p>

          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="login-email">Email</label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="votre@email.com"
                autoComplete="email"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="login-password">Mot de passe</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  style={{ paddingRight: '2.75rem', width: '100%' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  style={{
                    position: 'absolute', right: '0.25rem', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer',
                    padding: '0.5rem', display: 'flex', alignItems: 'center',
                  }}
                >
                  <EyeIcon off={showPassword} />
                </button>
              </div>
            </div>

            {error && (
              <div role="alert" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid var(--danger)', borderRadius: '8px', padding: '0.75rem', color: 'var(--danger)', fontSize: '0.875rem', marginBottom: '1rem' }}>
                {error}
              </div>
            )}

            <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '1rem', minHeight: '48px', background: accent, color: onAccent }} disabled={loading}>
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span className="login-spinner" aria-hidden="true"></span>
                  Connexion en cours...
                </span>
              ) : loginType === 'staff' ? 'Ouvrir le Scanner' : 'Se connecter'}
            </button>
          </form>

          {/* Password reset */}
          <div style={{ marginTop: '1.25rem', textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => setShowPasswordReset(v => !v)}
              aria-expanded={showPasswordReset}
              style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline', padding: '0.5rem' }}
            >
              Mot de passe oublié ?
            </button>

            {showPasswordReset && (
              <div style={{ marginTop: '0.75rem', padding: '1rem', background: 'rgba(99,102,241,0.1)', borderRadius: '8px', fontSize: '0.8rem', color: 'var(--muted)', textAlign: 'left' }}>
                {loginType === 'merchant'
                  ? 'Pas de panique ! Contactez l\'administration et nous réinitialiserons votre mot de passe rapidement.'
                  : loginType === 'manager'
                  ? 'Pas de panique ! Le siège de votre enseigne peut réinitialiser votre mot de passe.'
                  : 'Pas de panique ! Votre commerçant peut réinitialiser votre mot de passe depuis son dashboard.'}
              </div>
            )}
          </div>

          <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid var(--border)', textAlign: 'center', fontSize: '0.75rem', color: 'var(--muted)' }}>
            <Link href="/terms" style={{ color: 'var(--muted)', marginRight: '1rem' }}>Conditions</Link>
            <Link href="/privacy" style={{ color: 'var(--muted)' }}>Confidentialité</Link>
          </div>

          <LegacyStyle css={`
            .login-spinner {
              width: 16px; height: 16px;
              border: 2px solid rgba(255,255,255,0.3);
              border-top-color: currentColor;
              border-radius: 50%;
              animation: login-spin 0.8s linear infinite;
            }
            @keyframes login-spin { to { transform: rotate(360deg); } }
            @media (prefers-reduced-motion: reduce) {
              .login-spinner { animation-duration: 2s; }
            }
          `} />
        </div>
      </div>
    </div>
  );
}
